export interface VideoSourceEpisodeIdentity {
  episode: number;
  path: string;
  ext: string;
  mime: string;
  sha256: string;
  size_bytes?: number;
  mtime_ms?: number;
  /** Container duration used to reject impossible evidence timestamps. */
  duration_ms?: number;
}

export type VideoSourceEvolution =
  | { kind: "unchanged"; appended: [] }
  | { kind: "append"; appended: VideoSourceEpisodeIdentity[] }
  | { kind: "incompatible"; reason: string };

function identityMismatch(
  previous: VideoSourceEpisodeIdentity,
  current: VideoSourceEpisodeIdentity,
): string | null {
  if (previous.episode !== current.episode) {
    return `episode ${previous.episode} was replaced by episode ${current.episode}`;
  }
  if (previous.ext !== current.ext || previous.mime !== current.mime) {
    return `episode ${previous.episode} changed media type`;
  }
  if (previous.sha256 !== current.sha256) {
    return `episode ${previous.episode} content changed`;
  }
  return null;
}

export function classifyVideoSourceEvolution(
  previous: VideoSourceEpisodeIdentity[],
  current: VideoSourceEpisodeIdentity[],
): VideoSourceEvolution {
  if (current.length < previous.length) {
    return {
      kind: "incompatible",
      reason: `source removed ${previous.length - current.length} previously accepted episode(s)`,
    };
  }

  for (let index = 0; index < previous.length; index += 1) {
    const mismatch = identityMismatch(previous[index]!, current[index]!);
    if (mismatch) return { kind: "incompatible", reason: mismatch };
  }

  const appended = current.slice(previous.length);
  return appended.length === 0
    ? { kind: "unchanged", appended: [] }
    : { kind: "append", appended };
}

export function validateContinuousEpisodeSequence(
  episodes: ReadonlyArray<{ episode: number }>,
): string | null {
  for (let index = 0; index < episodes.length; index += 1) {
    const expected = index + 1;
    const actual = episodes[index]!.episode;
    if (actual !== expected) {
      return `expected episode ${expected}, found episode ${actual}`;
    }
  }
  return null;
}
